import { lazy, Suspense, useLayoutEffect, type ComponentType } from "react";
import { useAdmin } from "@/hooks/useAdmin";
import { CLIENT_AUDIT_EVENTS, recordSecurityEvent } from "@/lib/securityAudit";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Navigate, useLocation } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AnimatePresence, motion, MotionConfig } from "framer-motion";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { MotionPreferenceProvider, useMotionPreference } from "@/contexts/MotionPreferenceContext";
import { useProfile } from "@/hooks/useProfile";
import AppLayout from "@/components/AppLayout";
import ErrorBoundary from "@/components/ErrorBoundary";
import { PageLoader } from "@/components/PageLoader";
import { useVersionMismatchCheck } from "@/hooks/useVersionMismatchCheck";
import { safeRedirectPath } from "@/lib/safeRedirect";

function VersionMismatchWatcher() {
  useVersionMismatchCheck();
  return null;
}


const CookieConsent = lazy(() => import("@/components/CookieConsent"));
const OfflineBanner = lazy(() => import("@/components/OfflineBanner"));

// Retry wrapper for dynamic imports — handles stale cached chunks after redeploy
const lazyRetry = (factory: () => Promise<{ default: ComponentType<unknown> }>) =>
  lazy(() =>
    factory().catch((err) => {
      // If chunk fetch failed, hard-reload once to get fresh assets
      const key = "chunk-reload";
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, "1");
        window.location.reload();
        return new Promise(() => {}); // never resolves — page is reloading
      }
      sessionStorage.removeItem(key);
      throw err;
    })
  );

// Lazy-loaded pages
const Index = lazyRetry(() => import("./pages/Index"));
const Login = lazyRetry(() => import("./pages/Login"));
const Signup = lazyRetry(() => import("./pages/Signup"));
const AccessRequestStatus = lazyRetry(() => import("./pages/AccessRequestStatus"));
const GlobalRequestAccessDialog = lazyRetry(() => import("./components/landing/RequestAccessDialog"));
const ResetPassword = lazyRetry(() => import("./pages/ResetPassword"));
const Onboarding = lazyRetry(() => import("./pages/OnboardingRouter"));
const Dashboard = lazyRetry(() => import("./pages/Dashboard"));
const Checkin = lazyRetry(() => import("./pages/Checkin"));
const QuickStart = lazyRetry(() => import("./pages/QuickStart"));
const Reflection = lazyRetry(() => import("./pages/Reflection"));
const Insights = lazyRetry(() => import("./pages/Insights"));
const DailyPlan = lazyRetry(() => import("./pages/DailyPlan"));
const Settings = lazyRetry(() => import("./pages/Settings"));
// Pricing surfaces removed sitewide — mission-first, no paywall.
const Privacy = lazyRetry(() => import("./pages/Privacy"));
const Terms = lazyRetry(() => import("./pages/Terms"));
const Support = lazyRetry(() => import("./pages/Support"));
const Blog = lazyRetry(() => import("./pages/Blog"));
const BlogArticle = lazyRetry(() => import("./pages/BlogArticle"));
const Resources = lazyRetry(() => import("./pages/Resources"));
const NotFound = lazyRetry(() => import("./pages/NotFound"));
const CheckoutReturn = lazyRetry(() => import("./pages/CheckoutReturn"));
const Admin = lazyRetry(() => import("./pages/Admin"));
const AdminSecurity = lazyRetry(() => import("./pages/admin/SecurityDashboard"));
const AdminSeoTrends = lazyRetry(() => import("./pages/admin/SeoTrends"));
const AdminAccessRequests = lazyRetry(() => import("./pages/admin/AccessRequests"));
const AdminInviteCodes = lazyRetry(() => import("./pages/admin/InviteCodes"));
const AdminExperiments = lazyRetry(() => import("./pages/admin/ExperimentDashboard"));
const AdminClinicianLeads = lazyRetry(() => import("./pages/admin/ClinicianLeads"));
const AdminDeployWatch = lazyRetry(() => import("./pages/admin/DeployWatch"));
const AdminDeployStatus = lazyRetry(() => import("./pages/admin/DeployStatus"));
const AdminDeployHistory = lazyRetry(() => import("./pages/admin/DeployHistory"));
const AdminSupportInbox = lazyRetry(() => import("./pages/admin/SupportInbox"));
const AdminErrorMonitor = lazyRetry(() => import("./pages/admin/ErrorMonitor"));
const AdminAuditLog = lazyRetry(() => import("./pages/admin/AuditLog"));
const AdminEmailHealth = lazyRetry(() => import("./pages/admin/EmailHealth"));
const AdminVisitors = lazyRetry(() => import("./pages/admin/Visitors"));
const AdminVersionDiagnostics = lazyRetry(() => import("./pages/admin/VersionDiagnostics"));
const AdminLoopHealth = lazyRetry(() => import("./pages/admin/LoopHealth"));
const AdminUptime = lazyRetry(() => import("./pages/admin/Uptime"));

const Analytics = lazyRetry(() => import("./pages/Analytics"));
const Unsubscribe = lazyRetry(() => import("./pages/Unsubscribe"));
const Demo = lazyRetry(() => import("./pages/Demo"));
const WhoWeAre = lazyRetry(() => import("./pages/WhoWeAre"));
const Product = lazyRetry(() => import("./pages/Product"));
const ForClinicians = lazyRetry(() => import("./pages/ForClinicians"));
const ForCliniciansThankYou = lazyRetry(() => import("./pages/ForCliniciansThankYou"));
const CliniciansDataHandling = lazyRetry(() => import("./pages/CliniciansDataHandling"));
const ClinicianRoster = lazyRetry(() => import("./pages/ClinicianRoster"));
const ClinicianPortal = lazyRetry(() => import("./pages/ClinicianPortal"));
const ClinicianPortalPatient = lazyRetry(() => import("./pages/ClinicianPortalPatient"));
// MarketingPricing route removed sitewide.
const MsFatigueTracker = lazyRetry(() => import("./pages/pillars/MsFatigueTracker"));
const MsEnergyTracking = lazyRetry(() => import("./pages/pillars/MsEnergyTracking"));
const LivingWithMs = lazyRetry(() => import("./pages/pillars/LivingWithMs"));
const BlogPillarEnergyManagement = lazyRetry(() => import("./pages/blog/pillars/EnergyManagementMs"));
const BlogPillarMsFatigue = lazyRetry(() => import("./pages/blog/pillars/MsFatiguePillar"));
const PersonalLanding = lazyRetry(() => import("./pages/PersonalLanding"));
const Partners = lazyRetry(() => import("./pages/Partners"));
const Security = lazyRetry(() => import("./pages/Security"));
const Trust = lazyRetry(() => import("./pages/Trust"));
const ModelCard = lazyRetry(() => import("./pages/ModelCard"));
const Waitlist = lazyRetry(() => import("./pages/Waitlist"));
const Press = lazyRetry(() => import("./pages/Press"));
const Changelog = lazyRetry(() => import("./pages/Changelog"));
const EnergyGap = lazyRetry(() => import("./pages/EnergyGap"));
const Advisors = lazyRetry(() => import("./pages/Advisors"));
const Investors = lazyRetry(() => import("./pages/Investors"));
const OAuthConsent = lazyRetry(() => import("./pages/OAuthConsent"));
const McpDocs = lazyRetry(() => import("./pages/McpDocs"));
const StateOfMsEnergy = lazyRetry(() => import("./pages/StateOfMsEnergy"));
const Status = lazyRetry(() => import("./pages/Status"));
const LearnIndex = lazyRetry(() => import("./pages/LearnIndex"));
const LearnTopic = lazyRetry(() => import("./pages/LearnTopic"));
const Trademarks = lazyRetry(() => import("./pages/Trademarks"));
const CareSharePage = lazyRetry(() => import("./pages/CareSharePage"));
const RequestAccessRedirect = lazyRetry(() => import("@/components/RequestAccessRedirect"));
const StickyMobileCTA = lazy(() => import("@/components/marketing/StickyMobileCTA"));



const queryClient = new QueryClient();

const pageVariants = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
};

const pageTransition = { duration: 0.25, ease: "easeInOut" };

const PageWrapper = ({ children, context }: { children: React.ReactNode; context?: string }) => (
  <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={pageTransition}>
    <ErrorBoundary context={context}>{children}</ErrorBoundary>
  </motion.div>
);

const ProtectedRoute = ({ children, requireVerified = false }: { children: React.ReactNode; requireVerified?: boolean }) => {
  const { user, loading, emailVerified } = useAuth();
  const { profile, loading: profileLoading } = useProfile();

  if (loading || profileLoading) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (profile && !profile.onboarding_completed && window.location.pathname !== "/onboarding") return <Navigate to="/onboarding" replace />;
  if (requireVerified && !emailVerified) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
};

const AdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, loading: adminLoading } = useAdmin();
  if (authLoading || adminLoading) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!isAdmin) {
    void recordSecurityEvent(CLIENT_AUDIT_EVENTS.adminRouteBlocked, {
      summary: `Non-admin blocked from ${window.location.pathname}`,
    });
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
};

// Public marketing surfaces always render in the warm-paper "Workshop" light theme,
// regardless of the user's OS or saved preference. The authenticated app still
// honours the user's chosen theme.
const PUBLIC_SIGNUP_ENABLED = import.meta.env.VITE_SIGNUP_ENABLED === "true";

const MARKETING_PREFIXES = ["/", "/privacy", "/support", "/terms", "/blog", "/demo", "/login", "/signup", "/reset-password", "/unsubscribe", "/who-we-are", "/product", "/for-clinicians", "/for-clinicians/data-handling", "/for-clinicians/thank-you", "/clinicians", "/partners", "/security", "/press", "/ms-fatigue-tracker", "/ms-energy-tracking", "/living-with-ms", "/docs", "/status", "/state-of-ms-energy", "/learn", "/trademarks", "/trust", "/model-card", "/waitlist", "/energy-gap", "/changelog", "/advisors", "/investors"];

const isMarketingPath = (path: string) =>
  path === "/" ||
  MARKETING_PREFIXES.some((p) => p !== "/" && (path === p || path.startsWith(`${p}/`)));

const MarketingThemeLock = () => {
  const location = useLocation();
  useLayoutEffect(() => {
    const html = document.documentElement;
    if (isMarketingPath(location.pathname)) {
      html.dataset.forcedTheme = "light";
      html.classList.remove("dark");
    } else {
      delete html.dataset.forcedTheme;
      // Re-sync with saved/system theme when leaving a marketing route.
      const saved = (localStorage.getItem("theme") as "light" | "dark" | "system") || "system";
      const resolved = saved === "system"
        ? (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light")
        : saved;
      html.classList.toggle("dark", resolved === "dark");
    }
  }, [location.pathname]);
  return null;
};

const AppRoutes = () => {
  const { user } = useAuth();
  const location = useLocation();

  // If a signed-in user hits /login or /signup with a same-origin `?next=` path,
  // honor it (used by the OAuth consent flow) instead of forcing /dashboard.
  const nextFromQuery = safeRedirectPath(
    new URLSearchParams(location.search).get("next"),
  );
  const inviteCodeFromQuery = new URLSearchParams(location.search).get("code")?.trim();
  const publicSignupAllowed = PUBLIC_SIGNUP_ENABLED && Boolean(inviteCodeFromQuery);

  return (
    <Suspense fallback={<PageLoader className="min-h-screen" label="Loading page" />}>
      <MarketingThemeLock />
      <GlobalRequestAccessDialog />
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<PageWrapper><Index /></PageWrapper>} />
          <Route path="/login" element={user ? <Navigate to={nextFromQuery} replace /> : <PageWrapper><Login /></PageWrapper>} />
          <Route
            path="/signup"
            element={
              user ? (
                <Navigate to={nextFromQuery} replace />
              ) : publicSignupAllowed ? (
                <PageWrapper><Signup /></PageWrapper>
              ) : (
                <RequestAccessRedirect />
              )
            }
          />
          <Route path="/access-status" element={<PageWrapper><AccessRequestStatus /></PageWrapper>} />
          <Route path="/reset-password" element={<PageWrapper><ResetPassword /></PageWrapper>} />
          <Route path="/privacy" element={<PageWrapper><Privacy /></PageWrapper>} />
          <Route path="/blog" element={<PageWrapper><Blog /></PageWrapper>} />
          <Route path="/blog/pillar/energy-management-ms" element={<PageWrapper><BlogPillarEnergyManagement /></PageWrapper>} />
          <Route path="/blog/pillar/ms-fatigue" element={<PageWrapper><BlogPillarMsFatigue /></PageWrapper>} />
          <Route path="/blog/:slug" element={<PageWrapper><BlogArticle /></PageWrapper>} />

          <Route path="/who-we-are" element={<PageWrapper><WhoWeAre /></PageWrapper>} />
          <Route path="/product" element={<PageWrapper><Product /></PageWrapper>} />
          <Route path="/for-clinicians" element={<PageWrapper><ForClinicians /></PageWrapper>} />
          <Route path="/for-clinicians/data-handling" element={<PageWrapper><CliniciansDataHandling /></PageWrapper>} />
          <Route path="/for-clinicians/thank-you" element={<PageWrapper><ForCliniciansThankYou /></PageWrapper>} />
          <Route path="/clinicians/portal" element={<PageWrapper><ClinicianPortal /></PageWrapper>} />
          <Route path="/clinicians/roster" element={<ProtectedRoute><PageWrapper><ClinicianRoster /></PageWrapper></ProtectedRoute>} />
          <Route path="/clinicians/portal/patient/:id" element={<PageWrapper><ClinicianPortalPatient /></PageWrapper>} />
          <Route path="/terms" element={<PageWrapper><Terms /></PageWrapper>} />
          <Route path="/support" element={<PageWrapper><Support /></PageWrapper>} />
          <Route path="/onboarding" element={<PageWrapper><Onboarding /></PageWrapper>} />
          <Route path="/dashboard" element={<ProtectedRoute><AppLayout><PageWrapper><Dashboard /></PageWrapper></AppLayout></ProtectedRoute>} />
          <Route path="/checkin" element={<ProtectedRoute><AppLayout><PageWrapper><Checkin /></PageWrapper></AppLayout></ProtectedRoute>} />
          <Route path="/quickstart" element={<ProtectedRoute><AppLayout><PageWrapper><QuickStart /></PageWrapper></AppLayout></ProtectedRoute>} />
          <Route path="/morning" element={<Navigate to="/quickstart" replace />} />
          <Route path="/reflection" element={<ProtectedRoute><AppLayout><PageWrapper><Reflection /></PageWrapper></AppLayout></ProtectedRoute>} />
          <Route path="/plan" element={<ProtectedRoute><AppLayout><PageWrapper><DailyPlan /></PageWrapper></AppLayout></ProtectedRoute>} />
          <Route path="/insights" element={<ProtectedRoute><AppLayout><PageWrapper><Insights /></PageWrapper></AppLayout></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><AppLayout><PageWrapper><Settings /></PageWrapper></AppLayout></ProtectedRoute>} />
          <Route path="/pricing" element={<Navigate to="/" replace />} />
          <Route path="/billing" element={<Navigate to="/settings" replace />} />
          <Route path="/resources" element={<ProtectedRoute><AppLayout><PageWrapper><Resources /></PageWrapper></AppLayout></ProtectedRoute>} />
          <Route path="/checkout/return" element={<ProtectedRoute><AppLayout><PageWrapper><CheckoutReturn /></PageWrapper></AppLayout></ProtectedRoute>} />
          <Route path="/analytics" element={<ProtectedRoute><AppLayout><PageWrapper><Analytics /></PageWrapper></AppLayout></ProtectedRoute>} />
          <Route path="/admin" element={<AdminRoute><AppLayout><PageWrapper><Admin /></PageWrapper></AppLayout></AdminRoute>} />
          <Route path="/admin/security" element={<AdminRoute><AppLayout><PageWrapper context="admin/security"><AdminSecurity /></PageWrapper></AppLayout></AdminRoute>} />
          <Route path="/admin/seo-trends" element={<AdminRoute><AppLayout><PageWrapper context="admin/seo-trends"><AdminSeoTrends /></PageWrapper></AppLayout></AdminRoute>} />
          <Route path="/admin/access-requests" element={<AdminRoute><AppLayout><PageWrapper context="admin/access-requests"><AdminAccessRequests /></PageWrapper></AppLayout></AdminRoute>} />
          <Route path="/admin/invite-codes" element={<AdminRoute><AppLayout><PageWrapper context="admin/invite-codes"><AdminInviteCodes /></PageWrapper></AppLayout></AdminRoute>} />
          <Route path="/admin/experiments" element={<AdminRoute><AppLayout><PageWrapper context="admin/experiments"><AdminExperiments /></PageWrapper></AppLayout></AdminRoute>} />
          <Route path="/admin/clinician-leads" element={<AdminRoute><AppLayout><PageWrapper context="admin/clinician-leads"><AdminClinicianLeads /></PageWrapper></AppLayout></AdminRoute>} />
          <Route path="/admin/deploy-watch" element={<AdminRoute><AppLayout><PageWrapper context="admin/deploy-watch"><AdminDeployWatch /></PageWrapper></AppLayout></AdminRoute>} />
          <Route path="/admin/version" element={<AdminRoute><AppLayout><PageWrapper context="admin/version"><AdminVersionDiagnostics /></PageWrapper></AppLayout></AdminRoute>} />
          <Route path="/admin/deploy-status" element={<AdminRoute><AppLayout><PageWrapper context="admin/deploy-status"><AdminDeployStatus /></PageWrapper></AppLayout></AdminRoute>} />
          <Route path="/admin/deploy-history" element={<AdminRoute><AppLayout><PageWrapper context="admin/deploy-history"><AdminDeployHistory /></PageWrapper></AppLayout></AdminRoute>} />
          <Route path="/admin/support" element={<AdminRoute><AppLayout><PageWrapper context="admin/support"><AdminSupportInbox /></PageWrapper></AppLayout></AdminRoute>} />
          <Route path="/admin/audit-log" element={<AdminRoute><AppLayout><PageWrapper context="admin/audit-log"><AdminAuditLog /></PageWrapper></AppLayout></AdminRoute>} />
          <Route path="/admin/errors" element={<AdminRoute><AppLayout><PageWrapper context="admin/errors"><AdminErrorMonitor /></PageWrapper></AppLayout></AdminRoute>} />
          <Route path="/admin/uptime" element={<AdminRoute><AppLayout><PageWrapper context="admin/uptime"><AdminUptime /></PageWrapper></AppLayout></AdminRoute>} />
          <Route path="/admin/loop-health" element={<AdminRoute><AppLayout><PageWrapper context="admin/loop-health"><AdminLoopHealth /></PageWrapper></AppLayout></AdminRoute>} />
          <Route path="/admin/visitors" element={<AdminRoute><AppLayout><PageWrapper context="admin/visitors"><AdminVisitors /></PageWrapper></AppLayout></AdminRoute>} />
          <Route path="/admin/email-health" element={<AdminRoute><AppLayout><PageWrapper context="admin/email-health"><AdminEmailHealth /></PageWrapper></AppLayout></AdminRoute>} />

          <Route path="/unsubscribe" element={<PageWrapper><Unsubscribe /></PageWrapper>} />
          <Route path="/demo" element={<PageWrapper><Demo /></PageWrapper>} />
          <Route path="/ms-fatigue-tracker" element={<PageWrapper><MsFatigueTracker /></PageWrapper>} />
          <Route path="/ms-energy-tracking" element={<PageWrapper><MsEnergyTracking /></PageWrapper>} />
          <Route path="/living-with-ms" element={<PageWrapper><LivingWithMs /></PageWrapper>} />
          <Route path="/for/:slug" element={<PageWrapper><PersonalLanding /></PageWrapper>} />
          <Route path="/partners" element={<PageWrapper><Partners /></PageWrapper>} />
          <Route path="/security" element={<PageWrapper><Security /></PageWrapper>} />
          <Route path="/trust" element={<PageWrapper><Trust /></PageWrapper>} />
          <Route path="/model-card" element={<PageWrapper><ModelCard /></PageWrapper>} />
          <Route path="/waitlist" element={<PageWrapper><Waitlist /></PageWrapper>} />
          {/* /press temporarily unpublished; component retained for later restore. */}
          <Route path="/press" element={<Navigate to="/" replace />} />

          {/* Legacy / marketing aliases that used to 404 → send them somewhere useful. */}
          {/* Legacy pre-rebrand brand routes (ELMA → Myelina). */}
          <Route path="/elma" element={<Navigate to="/" replace />} />
          <Route path="/elma/*" element={<Navigate to="/" replace />} />
          <Route path="/features" element={<Navigate to="/#how-it-works" replace />} />
          <Route path="/faq" element={<Navigate to="/#faq" replace />} />
          <Route path="/how-it-works" element={<Navigate to="/#how-it-works" replace />} />
          <Route path="/principles" element={<Navigate to="/#principles" replace />} />
          <Route path="/clinicians" element={<Navigate to="/for-clinicians" replace />} />
          <Route path="/cookie-policy" element={<Navigate to="/privacy#cookies" replace />} />
          <Route path="/request-access" element={<RequestAccessRedirect />} />
          <Route path="/changelog" element={<PageWrapper><Changelog /></PageWrapper>} />
          <Route path="/energy-gap" element={<PageWrapper><EnergyGap /></PageWrapper>} />
          <Route path="/advisors" element={<PageWrapper><Advisors /></PageWrapper>} />
          <Route path="/investors" element={<PageWrapper><Investors /></PageWrapper>} />
          <Route path="/advisory-board" element={<Navigate to="/advisors" replace />} />
          <Route path="/.lovable/oauth/consent" element={<PageWrapper><OAuthConsent /></PageWrapper>} />
          <Route path="/docs/mcp" element={<PageWrapper><McpDocs /></PageWrapper>} />
          <Route path="/status" element={<PageWrapper><Status /></PageWrapper>} />
          <Route path="/state-of-ms-energy" element={<PageWrapper><StateOfMsEnergy /></PageWrapper>} />
          <Route path="/learn" element={<PageWrapper><LearnIndex /></PageWrapper>} />
          <Route path="/learn/:slug" element={<PageWrapper><LearnTopic /></PageWrapper>} />
          <Route path="/trademarks" element={<PageWrapper><Trademarks /></PageWrapper>} />
          <Route path="/care-share/:token" element={<PageWrapper><CareSharePage /></PageWrapper>} />



          <Route path="*" element={<PageWrapper><NotFound /></PageWrapper>} />
        </Routes>
      </AnimatePresence>
    </Suspense>
  );
};

const MotionConfigBridge = ({ children }: { children: React.ReactNode }) => {
  const { motionConfigValue } = useMotionPreference();
  return <MotionConfig reducedMotion={motionConfigValue}>{children}</MotionConfig>;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <MotionPreferenceProvider>
        {/* `reducedMotion` is driven by the user's in-app preference
            (Settings → Accessibility → Motion). When set to "system" it
            mirrors `prefers-reduced-motion`; "reduced" forces "always";
            "full" forces "never". See src/contexts/MotionPreferenceContext.tsx. */}
        <MotionConfigBridge>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <AuthProvider>
                <VersionMismatchWatcher />
                <a href="#main-content" className="skip-link">Skip to content</a>
                <Suspense fallback={null}>
                  <OfflineBanner />
                </Suspense>
                <div id="main-content">
                  <AppRoutes />
                </div>

                <Suspense fallback={null}>
                  <CookieConsent />
                </Suspense>
                <Suspense fallback={null}>
                  <StickyMobileCTA />
                </Suspense>
              </AuthProvider>
            </BrowserRouter>
          </TooltipProvider>
        </MotionConfigBridge>
      </MotionPreferenceProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
